import { cn } from "@/components/libs/cn";
import { motion } from "framer-motion";
import { type ReactNode, useMemo } from "react";

type LoadingDotsProps = {
  className?: string;
  dotSize?: number;
  dotColor?: string;
  children?: ReactNode;
};

const DOT_ANIMATION_Y_VALUE = 20;
const DOT_ANIMATION_REST_VALUE = -20;
const DOT_ANIMATION_DELAY_MULTIPLIER = 0.2;
const DOT_ANIMATION_DURATION = 1.5;
const DOT_COUNT = 3;
const DOT_GAP_CLASS = "gap-2";

export function LoadingDots({
  className,
  dotSize = 10,
  dotColor = "black",
  ...rest
}: LoadingDotsProps) {
  const dotVariants = useMemo(
    () => ({
      initial: { y: DOT_ANIMATION_REST_VALUE },
      animate: (i: number) => ({
        y: [
          DOT_ANIMATION_Y_VALUE,
          DOT_ANIMATION_REST_VALUE,
          DOT_ANIMATION_Y_VALUE,
        ],
        transition: {
          delay: i * DOT_ANIMATION_DELAY_MULTIPLIER,
          duration: DOT_ANIMATION_DURATION,
          ease: "easeInOut",
          repeat: Infinity,
        },
      }),
    }),
    [],
  );

  const dotsArray = useMemo(
    () => Array.from({ length: DOT_COUNT }, (_, i) => i),
    [],
  );

  return (
    <div
      className={cn(
        "flex items-center justify-center py-6",
        DOT_GAP_CLASS,
        className,
      )}
      {...rest}
    >
      {dotsArray.map((i) => (
        <motion.div
          key={i}
          custom={i}
          variants={dotVariants}
          initial="initial"
          animate="animate"
          className="rounded-full"
          style={{
            width: dotSize,
            height: dotSize,
            backgroundColor: dotColor,
          }}
        />
      ))}
    </div>
  );
}
